import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Plus,
  Trash2,
  Wifi,
  Tv,
  Radio,
  ChevronDown,
  ChevronRight,
  Package as PackageIcon,
  Wrench,
  Layers,
} from "lucide-react";
import { API_URL } from "../constants";
import { useRulesEngine } from "./useRulesEngine";
import { BlockConvergente } from "./BlockConvergente";
import { BlockMoviles } from "./BlockMoviles";
import { BlockSoloMovil } from "./BlockSoloMovil";
import { BlockCentralita } from "./BlockCentralita";
import { PackageSummary } from "./PackageSummary";

const genLineId = () => Math.random().toString(36).substr(2, 9);

/* ── Centralized pricing: ONLY depends on modo_builder, never on block ──
 * CONVERGENTE / A_MEDIDA → precio_paquete, fallback to precio_base.
 * STANDALONE             → precio_base. Null/undefined means NOT selectable.
 * Bonos, terminals, DDIs and discounts follow the same rule (they are plain
 * catalog services too); if they only expose `precio_base`, the fallback in
 * CONVERGENTE/A_MEDIDA picks it up automatically.
 * ─────────────────────────────────────────────────────────────────────── */
const getServicePrice = (service, mode) => {
  if (!service) return null;
  if (mode === "STANDALONE") {
    return (service.precio_base === null || service.precio_base === undefined)
      ? null
      : service.precio_base;
  }
  // CONVERGENTE & A_MEDIDA
  const paq = service.precio_paquete;
  if (paq !== null && paq !== undefined) return paq;
  const base = service.precio_base;
  if (base !== null && base !== undefined) return base;
  return null;
};

const isServiceSelectableForMode = (service, mode) =>
  getServicePrice(service, mode) !== null;

/* ── Centralized block visibility per mode ────────────────────────
 *  A = Paquete Convergente     B = Móviles Adicionales
 *  C = Solo Fibra              D = Solo Móvil
 *  E = TV                      F = Centralita        G = WiFi Pyme
 * ────────────────────────────────────────────────────────────────── */
const VISIBLE_BLOCKS_BY_MODE = {
  CONVERGENTE: ["A", "B", "E", "F", "G"],
  A_MEDIDA:    ["C", "D", "E", "F", "G"],
  STANDALONE:  ["C", "D", "E", "F", "G"],
};
const isBlockVisible = (key, mode) =>
  (VISIBLE_BLOCKS_BY_MODE[mode] || []).includes(key);

export const PackageBuilder = ({ catalog, onAddToQuote, formatCurrency: fmtC, onBuilderStateChange, customerId, manualDiscounts }) => {
  /* ── State ── */
  const [modoBuilder, setModoBuilder] = useState("CONVERGENTE"); // CONVERGENTE | A_MEDIDA | STANDALONE
  const [convergentPackages, setConvergentPackages] = useState([]);
  const [selectedConvergentId, setSelectedConvergentId] = useState("");
  const [extraMobiles, setExtraMobiles] = useState([{ key: genLineId(), serviceId: "", bonoId: "" }]);
  const [soloFibras, setSoloFibras] = useState([{ key: genLineId(), serviceId: "", ipAddOnId: "" }]);
  const [soloFibrasExtras, setSoloFibrasExtras] = useState({});
  const [soloMobiles, setSoloMobiles] = useState([{ key: genLineId(), serviceId: "", bonoId: "" }]);
  const [tvSelections, setTvSelections] = useState([""]);
  const [tvDiscountMap, setTvDiscountMap] = useState({});
  const [centralitaId, setCentralitaId] = useState("");
  const [centralitaKey] = useState(() => genLineId());
  const [terminalRows, setTerminalRows] = useState([]);
  const [ddiQuantity, setDdiQuantity] = useState(0);
  const [bonoCentralita, setBonoCentralita] = useState("");
  const [centralitaExtBonos, setCentralitaExtBonos] = useState({});
  // Centralita & WiFi discounts now follow the same TV-style "iterate all
  // catalog discount products, toggle each independently" pattern. The map
  // is keyed by the discount product id and contains booleans. Eligible
  // discounts are derived from the active business rules so the toggles
  // appear in CONVERGENTE / A_MEDIDA / STANDALONE the moment a discount
  // rule pairs them with the selected centralita / wifi product.
  const [centDiscountMap, setCentDiscountMap] = useState({});
  const [wifiId, setWifiId] = useState("");
  const [wifiDiscountMap, setWifiDiscountMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [internalCatalog, setInternalCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [existingCustomerServices, setExistingCustomerServices] = useState([]);
  const [existingLineBonos, setExistingLineBonos] = useState({});
  const [existingCentralitaBonos, setExistingCentralitaBonos] = useState({});
  const [openBlocks, setOpenBlocks] = useState({ A: true, B: false, C: false, D: false, E: false, F: false, G: false });
  const toggleBlock = (k) => setOpenBlocks(p => ({ ...p, [k]: !p[k] }));

  const { activeRules, loading: rulesLoading, checkRequiresCategory, filterByExclusivity, checkDiscountAvailable, getDiscountEligibility, validateCurrentServices } = useRulesEngine();

  /* ── Data loading ── */
  useEffect(() => {
    setCatalogLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/convergent-packages?is_active=true`).then(r => r.data).catch(() => []),
      axios.get(`${API_URL}/api/service-categories`).then(r => r.data).catch(() => []),
      axios.get(`${API_URL}/api/catalog-services`).then(r => r.data).catch(() => []),
    ]).then(([pkgs, cats, svcs]) => {
      setConvergentPackages(pkgs);
      setCategories(cats);
      setInternalCatalog(svcs);
    }).finally(() => setCatalogLoading(false));
  }, []);

  useEffect(() => {
    if (customerId) {
      axios.get(`${API_URL}/api/customer-services?customer_id=${customerId}&estado=ACTIVO`)
        .then(r => setExistingCustomerServices(r.data || []))
        .catch(() => setExistingCustomerServices([]));
    }
  }, [customerId]);

  const effectiveCatalog = internalCatalog || catalog;
  const getProduct = useCallback((id) => effectiveCatalog.find(s => s.id === id), [effectiveCatalog]);

  /* ── Categorized products ── */
  const cats = useMemo(() => {
    // A category can now be assigned to MULTIPLE blocks via `bloques_package_builder`.
    // Fallback to legacy single-value `bloque_package_builder` for backward compatibility.
    const getCategoryBlocks = (category) => {
      if (!category) return [];
      if (Array.isArray(category.bloques_package_builder) && category.bloques_package_builder.length) {
        return category.bloques_package_builder;
      }
      return category.bloque_package_builder ? [category.bloque_package_builder] : [];
    };
    // Inactive products / inactive categories must NOT show up as new selectable
    // options. We filter here (rather than at the API call) so `effectiveCatalog`
    // still contains inactive entries — that way `getProduct(id)` keeps finding
    // metadata for products that were marked inactive AFTER being selected on
    // an existing opportunity (preserves rendering of legacy data, PDFs, etc.).
    const isInactiveCategoryName = (name) => {
      const cat = categories.find(c => c.name === name);
      return cat ? cat.is_active === false : false;
    };
    const servicesByBlock = {};
    effectiveCatalog.forEach(service => {
      if (service.is_active === false) return;                  // inactive product
      if (isInactiveCategoryName(service.categoria)) return;    // belongs to inactive category
      const category = categories.find(c => c.name === service.categoria);
      const blocks = getCategoryBlocks(category);
      blocks.forEach(bloque => {
        if (!bloque || bloque === "OTRO") return;
        if (!servicesByBlock[bloque]) servicesByBlock[bloque] = [];
        // Dedupe: the same service never appears twice within a single block.
        if (!servicesByBlock[bloque].some(s => s.id === service.id)) {
          servicesByBlock[bloque].push(service);
        }
      });
    });
    return {
      fibras:       servicesByBlock["CONVERGENTE"] || [],
      mobilePaq:    servicesByBlock["MOVILES_PAQ"] || [],
      saFibras:     (servicesByBlock["SOLO_FIBRA"] || []).filter(s => {
        const cn = (s.categoria || "").toLowerCase();
        return cn.includes("fibra") || cn.includes("ftth");
      }),
      saFibrasExtras: (() => {
        const extras = (servicesByBlock["SOLO_FIBRA"] || []).filter(s => {
          const cn = (s.categoria || "").toLowerCase();
          return !cn.includes("fibra") && !cn.includes("ftth");
        });
        const grouped = {};
        extras.forEach(s => { if (!grouped[s.categoria]) grouped[s.categoria] = []; grouped[s.categoria].push(s); });
        return grouped;
      })(),
      saMobiles:    servicesByBlock["SOLO_MOVIL"] || [],
      bonosIntl:    servicesByBlock["BONO_MOVIL"] || [],
      tvProducts:   servicesByBlock["TV"] || [],
      tvDiscounts:  servicesByBlock["DESCUENTO_TV"] || [],
      centralitas:  servicesByBlock["CENTRALITA"] || [],
      terminals:    servicesByBlock["TERMINAL"] || [],
      ddis:         servicesByBlock["NUMERACION"] || [],
      bonosCent:    servicesByBlock["BONO_CENTRALITA"] || [],
      descCent:     servicesByBlock["DESCUENTO_CENTRALITA"] || [],
      wifiProducts: servicesByBlock["WIFI"] || [],
      descWifi:     servicesByBlock["DESCUENTO_WIFI"] || [],
      ipAddOns:     servicesByBlock["IP_ADICIONAL"] || [],
    };
  }, [effectiveCatalog, categories]);

  /* ── Existing customer lines ── */
  const existingMobileLines = useMemo(() =>
    existingCustomerServices.filter(cs => {
      const name = (cs.nombre_servicio || "").toLowerCase();
      return name.includes("móvil") || name.includes("movil") || name.includes("mobile");
    }),
  [existingCustomerServices]);

  const existingCentralitas = useMemo(() =>
    existingCustomerServices.filter(cs => {
      const name = (cs.nombre_servicio || "").toLowerCase();
      return name.includes("centralita");
    }),
  [existingCustomerServices]);

  const selectedConvergentPkg = useMemo(() =>
    convergentPackages.find(p => p.id === selectedConvergentId) || null,
  [convergentPackages, selectedConvergentId]);
  const pkgAValid = !!selectedConvergentPkg;

  /* ── Mode-driven helpers ── */
  // Block B (Móviles Adicionales) is visible only in CONVERGENTE mode (see
  // VISIBLE_BLOCKS_BY_MODE). It is enabled only once a convergent package is
  // actually selected.
  const blockBEnabled = pkgAValid;

  // Single source of truth for pricing. Depends ONLY on modo_builder.
  const priceOf = useCallback(
    (prod) => getServicePrice(prod, modoBuilder) ?? 0,
    [modoBuilder]
  );
  // Returns true when a service can actually be sold in the current mode.
  const isSelectable = useCallback(
    (prod) => isServiceSelectableForMode(prod, modoBuilder),
    [modoBuilder]
  );

  /* ── Reset state when mode changes to avoid cross-mode leftovers ── */
  useEffect(() => {
    // Block A — Paquete Convergente
    if (!isBlockVisible("A", modoBuilder)) setSelectedConvergentId("");
    // Block B — Móviles Adicionales
    if (!isBlockVisible("B", modoBuilder)) setExtraMobiles([{ key: genLineId(), serviceId: "", bonoId: "" }]);
    // Block C — Solo Fibra
    if (!isBlockVisible("C", modoBuilder)) { setSoloFibras([{ key: genLineId(), serviceId: "", ipAddOnId: "" }]); setSoloFibrasExtras({}); }
    // Block D — Solo Móvil
    if (!isBlockVisible("D", modoBuilder)) setSoloMobiles([{ key: genLineId(), serviceId: "", bonoId: "" }]);
    // Block E — TV
    if (!isBlockVisible("E", modoBuilder)) { setTvSelections([""]); setTvDiscountMap({}); }
    // Block F — Centralita
    if (!isBlockVisible("F", modoBuilder)) {
      setCentralitaId(""); setCentralitaExtBonos({}); setTerminalRows([]);
      setDdiQuantity(0); setBonoCentralita(""); setCentDiscountMap({});
    }
    // Block G — WiFi
    if (!isBlockVisible("G", modoBuilder)) { setWifiId(""); setWifiDiscountMap({}); }
  }, [modoBuilder]);

  /* ── Rules engine: currentServices ── */
  const currentServices = useMemo(() => {
    const services = [];
    const addById = (id) => {
      if (!id) return;
      const p = effectiveCatalog.find(s => s.id === id);
      if (p) services.push({ catalog_service_id: p.id, categoria: p.categoria });
    };
    if (selectedConvergentPkg) {
      selectedConvergentPkg.servicios.forEach(s => services.push({ catalog_service_id: s.catalog_service_id, categoria: s.categoria }));
    }
    extraMobiles.forEach(l => {
      addById(l.serviceId);
      if (l.bonoId) addById(l.bonoId);
      if (l.bonosByUnit) Object.values(l.bonosByUnit).forEach(addById);
    });
    soloFibras.forEach(l => { addById(l.serviceId); if (l.ipAddOnId) addById(l.ipAddOnId); });
    Object.values(soloFibrasExtras).forEach(addById);
    soloMobiles.forEach(l => {
      addById(l.serviceId);
      if (l.bonoId) addById(l.bonoId);
      if (l.bonosByUnit) Object.values(l.bonosByUnit).forEach(addById);
    });
    tvSelections.forEach(addById);
    addById(centralitaId);
    addById(bonoCentralita);
    // centralitaExtBonos: { [extLineaId]: string[] } — array allows multiple bonos per extension.
    Object.values(centralitaExtBonos).forEach(arr => {
      (Array.isArray(arr) ? arr : (arr ? [arr] : [])).forEach(addById);
    });
    addById(wifiId);
    Object.values(existingLineBonos).forEach(addById);
    Object.values(existingCentralitaBonos).forEach(addById);
    return services;
  }, [selectedConvergentPkg, effectiveCatalog, extraMobiles, soloFibras, soloFibrasExtras, soloMobiles, tvSelections, centralitaId, bonoCentralita, wifiId, existingLineBonos, existingCentralitaBonos, centralitaExtBonos]);

  /* ── Cascading resets ── */
  useEffect(() => { if (!blockBEnabled) setExtraMobiles([{ key: genLineId(), serviceId: "", bonoId: "" }]); }, [blockBEnabled]);
  useEffect(() => { if (!centralitaId) { setCentDiscountMap({}); setBonoCentralita(""); setCentralitaExtBonos({}); } }, [centralitaId]);
  useEffect(() => { if (!wifiId) setWifiDiscountMap({}); }, [wifiId]);

  useEffect(() => {
    if (bonoCentralita) {
      const cat = getProduct(bonoCentralita)?.categoria;
      if (cat && !checkRequiresCategory(cat, currentServices).permitido) setBonoCentralita("");
    }
  }, [bonoCentralita, currentServices, checkRequiresCategory, getProduct]);

  useEffect(() => {
    if (tvSelections.some(id => id)) {
      const cat = cats.tvProducts[0]?.categoria;
      if (cat && !checkRequiresCategory(cat, currentServices).permitido) { setTvSelections([""]); setTvDiscountMap({}); }
    }
  }, [currentServices, checkRequiresCategory, cats.tvProducts, tvSelections]);

  /* ── Discount helpers — TV-style (rule-driven, multi-discount) ──
   * For Centralita & WiFi we iterate every catalog discount product whose
   * DESCUENTO_CONDICIONAL rule pairs it with the currently selected
   * centralita / wifi (via `producto_objetivo_id`). Rules with no
   * `producto_objetivo_id` are treated as generic and surfaced for any
   * selected product. This mirrors how TV discounts are listed and works
   * uniformly across CONVERGENTE / A_MEDIDA / STANDALONE — the only
   * pre-condition is that a centralita / wifi product is actually picked.
   * Substring matching on product names (1x/3x/5x/1AP/2AP) was removed
   * earlier; all references come from the rule record itself.
   */
  const eligibleCentDiscounts = useMemo(() => {
    if (!centralitaId || !cats.descCent || cats.descCent.length === 0) return [];
    return cats.descCent.filter(disc => {
      const rule = activeRules.find(r =>
        r.tipo === "DESCUENTO_CONDICIONAL" && r.condicion_producto_id === disc.id
      );
      if (!rule) return false;
      if (!rule.producto_objetivo_id) return true; // generic discount
      return rule.producto_objetivo_id === centralitaId;
    });
  }, [activeRules, cats.descCent, centralitaId]);

  const eligibleWifiDiscounts = useMemo(() => {
    if (!wifiId || !cats.descWifi || cats.descWifi.length === 0) return [];
    return cats.descWifi.filter(disc => {
      const rule = activeRules.find(r =>
        r.tipo === "DESCUENTO_CONDICIONAL" && r.condicion_producto_id === disc.id
      );
      if (!rule) return false;
      if (!rule.producto_objetivo_id) return true;
      return rule.producto_objetivo_id === wifiId;
    });
  }, [activeRules, cats.descWifi, wifiId]);

  // Selected TV services (from the catalog bucket) currently in currentServices.
  // Used by the count-based TV discount eligibility.
  const pairedTvMatches = useMemo(() => {
    const tvIds = new Set(cats.tvProducts.map(p => p.id));
    return currentServices.filter(s => tvIds.has(s.catalog_service_id));
  }, [currentServices, cats.tvProducts]);

  const tvEligibilityFor = useCallback(
    (tvDisc) => getDiscountEligibility(tvDisc, currentServices, { pairedMatches: pairedTvMatches }),
    [getDiscountEligibility, currentServices, pairedTvMatches]
  );

  useEffect(() => {
    cats.tvDiscounts.forEach(d => {
      if (tvDiscountMap[d.id] && !tvEligibilityFor(d).disponible)
        setTvDiscountMap(prev => ({ ...prev, [d.id]: false }));
    });
  }, [tvDiscountMap, cats.tvDiscounts, tvEligibilityFor]);
  // Auto-disable Cent / WiFi discount toggles when their requirement disappears
  // (e.g. fiber removed, target product changed). Mirrors the TV pattern above.
  useEffect(() => {
    Object.keys(centDiscountMap).forEach(discId => {
      if (!centDiscountMap[discId]) return;
      const stillEligible = eligibleCentDiscounts.some(d => d.id === discId);
      const available = checkDiscountAvailable(discId, currentServices).disponible;
      if (!stillEligible || !available) {
        setCentDiscountMap(prev => ({ ...prev, [discId]: false }));
      }
    });
  }, [centDiscountMap, eligibleCentDiscounts, currentServices, checkDiscountAvailable]);
  useEffect(() => {
    Object.keys(wifiDiscountMap).forEach(discId => {
      if (!wifiDiscountMap[discId]) return;
      const stillEligible = eligibleWifiDiscounts.some(d => d.id === discId);
      const available = checkDiscountAvailable(discId, currentServices).disponible;
      if (!stillEligible || !available) {
        setWifiDiscountMap(prev => ({ ...prev, [discId]: false }));
      }
    });
  }, [wifiDiscountMap, eligibleWifiDiscounts, currentServices, checkDiscountAvailable]);

  /* ── Computed blocks ── */
  const computed = useMemo(() => {
    const blocks = [];
    const addItem = (id, nombre, precio, qty, bloque, linea, multi, isDiscount, lineaId, asociadoALineaId, esLineaExistente) => ({
      id, nombre, precio, qty, bloque,
      linea: linea || null, multi: multi || null, isDiscount: isDiscount || false,
      lineaId: lineaId || null, asociadoALineaId: asociadoALineaId || null,
      esLineaExistente: esLineaExistente || false,
    });
    const addMultiUnit = (prod, bloque, baseLineaId) => {
      const items = [];
      const units = prod.unidades_por_seleccion || 1;
      const price = priceOf(prod);
      if (units > 1) {
        const cleanName = prod.nombre.replace(/^\(\d+x\)\s*/i, "");
        for (let i = 0; i < units; i++) {
          const lid = i === 0 ? baseLineaId : `${baseLineaId}_u${i + 1}`;
          items.push(addItem(prod.id, cleanName, price, 1, bloque, i + 1, units, false, lid));
        }
      } else {
        items.push(addItem(prod.id, prod.nombre, price, 1, bloque, null, null, false, baseLineaId));
      }
      return items;
    };

    /* Block A — Convergent package */
    if (isBlockVisible("A", modoBuilder) && selectedConvergentPkg) {
      // Each service in the bundle gets a stable lineaId so manual discounts
      // edited in the bottom table can be mapped back by linea_id (same
      // mechanism every other block uses). Keyed on pkg id + service index
      // so it stays stable across re-renders and unique when the bundle has
      // duplicate catalog_service_ids.
      const pkgLid = (idx) => `pkgA_${selectedConvergentPkg.id}_s${idx}`;
      const items = selectedConvergentPkg.servicios.map((svc, idx) =>
        addItem(svc.catalog_service_id, svc.nombre, svc.precio, 1, "PAQUETE_CONVERGENTE", svc.linea_numero, null, false, pkgLid(idx))
      );
      blocks.push({ key: "A", name: "PAQUETE CONVERGENTE", items, subtotal: selectedConvergentPkg.precio_total });
    }

    /* Block B — Móviles Adicionales */
    if (isBlockVisible("B", modoBuilder) && blockBEnabled) {
      const items = []; let sub = 0;
      extraMobiles.forEach(line => {
        if (line.serviceId) {
          const p = getProduct(line.serviceId);
          if (p) {
            items.push(...addMultiUnit(p, "MOVILES_ADICIONALES", line.key));
            sub += priceOf(p) * (p.unidades_por_seleccion || 1);
            // Per-unit bonos: each generated mobile sub-line (línea 1..N) can
            // carry its own bono. Legacy singleton `line.bonoId` maps to línea 1.
            const units = p.unidades_por_seleccion || 1;
            const bonosMap = line.bonosByUnit || (line.bonoId ? { [line.key]: line.bonoId } : {});
            for (let u = 0; u < units; u++) {
              const uLid = u === 0 ? line.key : `${line.key}_u${u + 1}`;
              const bId = bonosMap[uLid];
              if (!bId) continue;
              const bono = getProduct(bId);
              if (!bono) continue;
              const bp = priceOf(bono);
              items.push(addItem(bono.id, bono.nombre, bp, 1, "MOVILES_ADICIONALES", null, null, false, `${uLid}_bono`, uLid));
              sub += bp;
            }
          }
        }
      });
      Object.entries(existingLineBonos).forEach(([existLineaId, bonoId]) => {
        if (bonoId) {
          const bono = getProduct(bonoId);
          if (bono) { const bp = priceOf(bono); items.push(addItem(bono.id, bono.nombre, bp, 1, "MOVILES_ADICIONALES", null, null, false, `exist_${existLineaId}_bono`, existLineaId, true)); sub += bp; }
        }
      });
      if (items.length > 0) blocks.push({ key: "B", name: "MOVILES ADICIONALES", items, subtotal: sub });
    }

    /* Block C — Solo Fibra */
    if (isBlockVisible("C", modoBuilder)) { const items = []; let sub = 0;
      soloFibras.forEach((line) => {
        if (!line?.serviceId) return;
        const p = getProduct(line.serviceId);
        if (!p) return;
        items.push(...addMultiUnit(p, "SOLO_FIBRA", line.key));
        sub += priceOf(p) * (p.unidades_por_seleccion || 1);
        // Fixed IP add-on associated to this fiber line (catalog-driven).
        if (line.ipAddOnId) {
          const ip = getProduct(line.ipAddOnId);
          if (ip) {
            const ipPrice = priceOf(ip);
            items.push(addItem(ip.id, ip.nombre, ipPrice, 1, "SOLO_FIBRA", null, null, false, `${line.key}_ip`, line.key));
            sub += ipPrice;
          }
        }
      });
      Object.entries(soloFibrasExtras).forEach(([catName, id]) => { if (id) { const p = getProduct(id); if (p) { items.push(...addMultiUnit(p, "SOLO_FIBRA", `sfe_${catName}`)); sub += priceOf(p) * (p.unidades_por_seleccion || 1); } } });
      if (items.length > 0) blocks.push({ key: "C", name: "FIBRA", items, subtotal: sub });
    }

    /* Block D — Solo Móvil */
    if (isBlockVisible("D", modoBuilder)) { const items = []; let sub = 0;
      soloMobiles.forEach(line => {
        if (line.serviceId) {
          const p = getProduct(line.serviceId);
          if (p) {
            items.push(...addMultiUnit(p, "SOLO_MOVIL", line.key));
            sub += priceOf(p) * (p.unidades_por_seleccion || 1);
            // Per-unit bonos (see Block B for rationale).
            const units = p.unidades_por_seleccion || 1;
            const bonosMap = line.bonosByUnit || (line.bonoId ? { [line.key]: line.bonoId } : {});
            for (let u = 0; u < units; u++) {
              const uLid = u === 0 ? line.key : `${line.key}_u${u + 1}`;
              const bId = bonosMap[uLid];
              if (!bId) continue;
              const bono = getProduct(bId);
              if (!bono) continue;
              const bp = priceOf(bono);
              items.push(addItem(bono.id, bono.nombre, bp, 1, "SOLO_MOVIL", null, null, false, `${uLid}_bono`, uLid));
              sub += bp;
            }
          }
        }
      });
      if (!blockBEnabled) {
        Object.entries(existingLineBonos).forEach(([existLineaId, bonoId]) => {
          if (bonoId) {
            const bono = getProduct(bonoId);
            if (bono) { const bp = priceOf(bono); items.push(addItem(bono.id, bono.nombre, bp, 1, "SOLO_MOVIL", null, null, false, `exist_${existLineaId}_bono`, existLineaId, true)); sub += bp; }
          }
        });
      }
      if (items.length > 0) blocks.push({ key: "D", name: "MOVIL", items, subtotal: sub });
    }

    /* Block E — TV */
    if (isBlockVisible("E", modoBuilder)) { const tvCat = cats.tvProducts[0]?.categoria;
      const tvCheck = tvCat ? checkRequiresCategory(tvCat, currentServices) : { permitido: true };
      if (tvCheck.permitido) {
        const items = []; let sub = 0;
        tvSelections.forEach((id, i) => { if (id) { const p = getProduct(id); if (p) { items.push(...addMultiUnit(p, "TV", `tv_${i}`)); sub += priceOf(p) * (p.unidades_por_seleccion || 1); } } });
        // Count-based TV discount emission: one individual discount line per
        // eligible (required, paired-TV) pair. Emission is generic and
        // catalog/rule-driven — no hardcoded fibers, TVs, names or IDs.
        const tvLineItems = items.filter(it => !it.isDiscount);
        cats.tvDiscounts.forEach(tvDisc => {
          if (!tvDiscountMap[tvDisc.id]) return;
          const elig = tvEligibilityFor(tvDisc);
          if (!elig.disponible) return;
          const dp = priceOf(tvDisc);
          for (let k = 0; k < elig.eligibleCount; k++) {
            const paired = tvLineItems[k];
            items.push(addItem(
              tvDisc.id, tvDisc.nombre, dp, 1, "TV",
              null, null, true,
              `${tvDisc.id}_app_${k}`,       // unique linea_id per application
              paired?.lineaId || null        // associate to the paired TV line
            ));
            sub += dp;
          }
        });
        if (items.length > 0) blocks.push({ key: "E", name: "TV", items, subtotal: sub });
      }
    }

    /* Block F — Centralita */
    if (isBlockVisible("F", modoBuilder)) { const items = []; let sub = 0;
      if (centralitaId) {
        const p = getProduct(centralitaId);
        if (p) {
          // Centralita: ONE commercial line, qty=1, full catalog price.
          // `unidades_por_seleccion` only drives how many Ext selectors
          // appear in the UI — it is NOT a price multiplier for the budget.
          const centPrice = priceOf(p);
          const centUnits = p.unidades_por_seleccion || 1;
          const cleanName = p.nombre.replace(/^\(\d+x\)\s*/i, "");
          items.push(addItem(
            p.id, cleanName, centPrice, 1, "CENTRALITA",
            null, centUnits > 1 ? centUnits : null, false, centralitaKey
          ));
          sub += centPrice;

          // Extension bonos — associate all to the single centralita parent
          // (`centralitaKey`) so the summary renders them nested under the
          // centralita. Label with "(Ext N)" when the product has multiple
          // extensions so the user can tell them apart.
          const extLineaIds = [];
          for (let i = 0; i < centUnits; i++) {
            extLineaIds.push(i === 0 ? centralitaKey : `${centralitaKey}_u${i + 1}`);
          }
          extLineaIds.forEach((extLid, idx) => {
            const raw = centralitaExtBonos[extLid];
            const bIds = Array.isArray(raw) ? raw : (raw ? [raw] : []);
            bIds.forEach((bId) => {
              if (!bId) return;
              const bp = getProduct(bId);
              if (!bp) return;
              const bprice = priceOf(bp);
              const displayName = centUnits > 1 ? `${bp.nombre} (Ext ${idx + 1})` : bp.nombre;
              // Associate each extension bono to its OWN extension linea_id so the
              // backend bono_map doesn't collapse multiple bonos into one key when
              // provisions.build_technical_items expands the centralita into N items.
              // linea_id uses the bono product id as part of the suffix to stay stable
              // and distinct when several bonos live on the same extension.
              items.push(addItem(
                bp.id, displayName, bprice, 1, "CENTRALITA",
                null, null, false, `${extLid}_bono_${bId}`, extLid
              ));
              sub += bprice;
            });
          });
        }
      }
      // Terminales IP — dynamic. Each row picks its own catalog terminal.
      terminalRows.forEach((t, i) => {
        if (!t?.serviceId) return;
        const terminal = getProduct(t.serviceId);
        if (!terminal) return;
        const price = priceOf(terminal);
        const qty = Math.max(1, parseInt(t.qty) || 1);
        items.push(addItem(terminal.id, terminal.nombre, price, qty, "CENTRALITA", null, null, false, `term_${i}`));
        sub += price * qty;
      });
      const ddi = cats.ddis[0];
      if (ddiQuantity > 0 && ddi) { const price = priceOf(ddi); items.push(addItem(ddi.id, ddi.nombre, price, ddiQuantity, "CENTRALITA", null, null, false, "ddi")); sub += price * ddiQuantity; }
      // Multi-discount emission: one line per active toggle in centDiscountMap
      // whose rule eligibility is currently satisfied. Mirrors the TV pattern.
      eligibleCentDiscounts.forEach(d => {
        if (!centDiscountMap[d.id]) return;
        if (!checkDiscountAvailable(d.id, currentServices).disponible) return;
        const dp = priceOf(d);
        items.push(addItem(d.id, d.nombre, dp, 1, "CENTRALITA", null, null, true, `${d.id}_app`));
        sub += dp;
      });
      Object.entries(existingCentralitaBonos).forEach(([existLineaId, bonoId]) => {
        if (bonoId) { const bono = getProduct(bonoId); if (bono) { const bp = priceOf(bono); items.push(addItem(bono.id, bono.nombre, bp, 1, "CENTRALITA", null, null, false, `exist_${existLineaId}_bono`, existLineaId, true)); sub += bp; } }
      });
      if (items.length > 0) blocks.push({ key: "F", name: "CENTRALITA", items, subtotal: sub });
    }

    /* Block G — WiFi Pyme */
    if (isBlockVisible("G", modoBuilder)) { const items = []; let sub = 0;
      if (wifiId) { const p = getProduct(wifiId); if (p) { items.push(...addMultiUnit(p, "WIFI_PYME", "wifi")); sub += priceOf(p) * (p.unidades_por_seleccion || 1); } }
      // Multi-discount emission for WiFi Pyme (TV-style).
      eligibleWifiDiscounts.forEach(d => {
        if (!wifiDiscountMap[d.id]) return;
        if (!checkDiscountAvailable(d.id, currentServices).disponible) return;
        const dp = priceOf(d);
        items.push(addItem(d.id, d.nombre, dp, 1, "WIFI_PYME", null, null, true, `${d.id}_app`));
        sub += dp;
      });
      if (items.length > 0) blocks.push({ key: "G", name: "WIFI PYME", items, subtotal: sub });
    }

    const total = blocks.reduce((s, b) => s + b.subtotal, 0);
    return { blocks, total, sinIva: total / 1.21, iva: total - total / 1.21 };
  }, [selectedConvergentPkg, blockBEnabled, modoBuilder, extraMobiles, soloFibras, soloFibrasExtras, soloMobiles,
      tvSelections, tvDiscountMap, centralitaId, centralitaKey, terminalRows, ddiQuantity, bonoCentralita, centralitaExtBonos, centDiscountMap,
      wifiId, wifiDiscountMap, getProduct, priceOf, cats, eligibleCentDiscounts, eligibleWifiDiscounts,
      checkRequiresCategory, checkDiscountAvailable, tvEligibilityFor, currentServices,
      existingLineBonos, existingCentralitaBonos]);

  const subtotals = useMemo(() => {
    const s = { A: null, B: null, C: null, D: null, E: null, F: null, G: null };
    computed.blocks.forEach(b => { s[b.key] = b.subtotal; });
    return s;
  }, [computed]);

  /* ── Final rules-engine validation against the complete selection ── */
  const validation = useMemo(
    () => validateCurrentServices(currentServices),
    [validateCurrentServices, currentServices]
  );

  useEffect(() => {
    if (onBuilderStateChange) onBuilderStateChange(computed.blocks.length > 0, validation);
  }, [computed.blocks.length, validation, onBuilderStateChange]);

  /* ── Sync to parent ── */
  const onAddToQuoteRef = useRef(onAddToQuote);
  onAddToQuoteRef.current = onAddToQuote;

  const blocksKey = useMemo(() =>
    JSON.stringify(computed.blocks.map(b => b.items.map(i => [i.id, i.precio, i.qty, i.linea, i.lineaId, i.asociadoALineaId, i.esLineaExistente]))),
  [computed.blocks]);

  useEffect(() => {
    const services = computed.blocks.flatMap(b =>
      b.items.map(it => ({
        catalog_service_id: it.id,
        nombre: it.nombre,
        precio: it.precio,
        cantidad: it.qty,
        descuento: 0,
        bloque: it.bloque,
        origen: modoBuilder,
        linea_id: it.lineaId || undefined,
        linea_numero: it.linea,
        asociado_a_linea_id: it.asociadoALineaId || undefined,
        es_linea_existente: it.esLineaExistente || false,
      }))
    );
    onAddToQuoteRef.current(services);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksKey, modoBuilder]);

  /* ── Renderers ── */
  const renderRows = (arr, setArr, opts, max = Infinity, placeholder, _unusedPriceKey, addLabel) => {
    // Centralized pricing & selectability per current mode
    const visibleOpts = opts.filter(p => isSelectable(p));
    return (
    <div className="space-y-2">
      {arr.map((val, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1">
            <Select value={val} onValueChange={v => { const n = [...arr]; n[i] = v; setArr(n); }}>
              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm" data-testid={`row-select-${i}`}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                {visibleOpts.map(p => <SelectItem key={p.id} value={p.id} className="text-white text-sm">{p.nombre} — {fmtC(priceOf(p))}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {val && getProduct(val) && <span className="text-slate-500 text-xs min-w-[65px] text-right">{fmtC(priceOf(getProduct(val)))}</span>}
          <button onClick={() => setArr(arr.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 p-1" data-testid={`row-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {arr.length < max && (
        <Button variant="ghost" size="sm" onClick={() => setArr([...arr, ""])} className="text-[#03BEE4] text-xs h-7" data-testid="add-row-btn">
          <Plus className="h-3 w-3 mr-1" />{addLabel}
        </Button>
      )}
    </div>
    );
  };

  const renderDiscount = (checked, onChange, discountProduct, label, extraInfo) => {
    const check = checkDiscountAvailable(discountProduct?.id, currentServices);
    return (
      <div className={`flex items-center gap-2 p-2 rounded-lg ${!check.disponible ? "bg-[#0A0A35]/20" : "bg-[#1B1B4B]/50"}`}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={!check.disponible} className="h-4 w-4 rounded border-[#1E1E5F] accent-[#42E695]" />
        <span className={`text-sm ${!check.disponible ? "text-slate-500" : "text-white"}`}>{label} ({fmtC(priceOf(discountProduct))})</span>
        {extraInfo && <span className="text-xs text-[#03BEE4] ml-2">{extraInfo}</span>}
        {check.disponible && checked && <span className="text-xs text-[#42E695] ml-auto">{check.mensaje || "Descuento aplicado"}</span>}
        {!check.disponible && check.mensaje && <span className="text-xs text-slate-500 ml-auto">{check.mensaje}</span>}
      </div>
    );
  };

  const renderBlockHeader = (key, icon, name, disabled, tip) => (
    <div
      onClick={() => !disabled && toggleBlock(key)}
      className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${disabled ? "opacity-50 bg-[#0A0A35]/30 cursor-not-allowed" : "bg-[#1B1B4B] hover:bg-[#1B1B4B]/80"}`}
      data-testid={`block-${key}-header`}
    >
      <div className="flex items-center gap-2">
        {openBlocks[key] && !disabled ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        {icon}
        <span className="text-white font-medium text-sm">{name}</span>
        {disabled && tip && <span className="text-xs text-red-400/70 ml-2">{tip}</span>}
      </div>
      <span className={`font-semibold text-sm ${subtotals[key] != null ? "text-[#42E695]" : "text-slate-500"}`}>
        {subtotals[key] != null ? fmtC(subtotals[key]) : "\u2014"}
      </span>
    </div>
  );

  if (rulesLoading || catalogLoading) return (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#42E695]"></div>
      <span className="ml-3 text-slate-400 text-sm">Cargando catálogo...</span>
    </div>
  );

  /* ── Dynamic checks ── */
  const tvCategory = cats.tvProducts[0]?.categoria;
  const tvCheck = tvCategory ? checkRequiresCategory(tvCategory, currentServices) : { permitido: true, mensaje: null };
  const bonoCentCategory = cats.bonosCent[0]?.categoria;
  const bonoCentCheck = bonoCentCategory ? checkRequiresCategory(bonoCentCategory, currentServices) : { permitido: true, mensaje: null };
  const bonoIntlCategory = cats.bonosIntl[0]?.categoria;
  const bonoIntlCheckB = bonoIntlCategory ? checkRequiresCategory(bonoIntlCategory, currentServices) : { permitido: true, mensaje: null };
  const bonoIntlCheckD = bonoIntlCategory ? checkRequiresCategory(bonoIntlCategory, currentServices) : { permitido: true, mensaje: null };
  const mobilesForB = filterByExclusivity(cats.mobilePaq, currentServices);

  return (
    <div className="flex gap-4" data-testid="package-builder">
      <div className="w-3/5 space-y-3">
        {/* Mode selector */}
        <div className="flex items-center gap-1 bg-[#0A0A35] border border-[#1E1E5F] rounded-lg p-1" data-testid="modo-builder-tabs">
          {[
            { id: "CONVERGENTE", label: "Paquete convergente", icon: <PackageIcon className="h-3.5 w-3.5" /> },
            { id: "A_MEDIDA",    label: "Paquete a medida",    icon: <Wrench className="h-3.5 w-3.5" /> },
            { id: "STANDALONE",  label: "Standalone",          icon: <Layers className="h-3.5 w-3.5" /> },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setModoBuilder(t.id)}
              data-testid={`modo-tab-${t.id.toLowerCase()}`}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded transition-colors ${
                modoBuilder === t.id
                  ? "bg-[#1B1B4B] text-[#03BEE4] shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Block A — Convergent package */}
        {isBlockVisible("A", modoBuilder) && (
          <BlockConvergente
            convergentPackages={convergentPackages}
            selectedConvergentId={selectedConvergentId}
            setSelectedConvergentId={setSelectedConvergentId}
            selectedConvergentPkg={selectedConvergentPkg}
            fmtC={fmtC}
            renderBlockHeader={renderBlockHeader}
            openBlocks={openBlocks}
          />
        )}

        {/* Block B — Móviles Adicionales */}
        {isBlockVisible("B", modoBuilder) && (
          <BlockMoviles
            lines={extraMobiles}
            setLines={setExtraMobiles}
            products={mobilesForB}
            bonoProducts={cats.bonosIntl}
            bonoCheck={bonoIntlCheckB}
            existingLines={existingMobileLines}
            existingBonos={existingLineBonos}
            setExistingBonos={setExistingLineBonos}
            getProduct={getProduct}
            fmtC={fmtC}
            renderBlockHeader={renderBlockHeader}
            openBlocks={openBlocks}
            pkgAValid={blockBEnabled}
            placeholder="Selecciona móvil..."
            getPrice={priceOf}
            disabledTip={modoBuilder === "CONVERGENTE" ? "Requiere paquete convergente" : ""}
          />
        )}

        {/* Block C — Solo Fibra */}
        {isBlockVisible("C", modoBuilder) && (
        <div className="border border-[#1E1E5F] rounded-lg overflow-hidden" data-testid="block-C">
          {renderBlockHeader("C", <Wifi className="h-4 w-4 text-[#03BEE4]" />, modoBuilder === "STANDALONE" ? "Solo Fibra" : "Fibra")}
          {openBlocks.C && (
            <div className="p-4 space-y-3 bg-[#0A0A35]/30">
              {/* Fiber lines — each row can optionally carry a fixed IP add-on */}
              <div className="space-y-2">
                {soloFibras.map((line, i) => {
                  const fiberOptions = cats.saFibras.filter(isSelectable);
                  const ipOptions    = cats.ipAddOns.filter(isSelectable);
                  const selectedFiber = line.serviceId ? getProduct(line.serviceId) : null;
                  const selectedIp    = line.ipAddOnId ? getProduct(line.ipAddOnId)    : null;
                  return (
                    <div key={line.key} className="space-y-1" data-testid={`fiber-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Select
                            value={line.serviceId || ""}
                            onValueChange={v => {
                              const n = [...soloFibras]; n[i] = { ...n[i], serviceId: v }; setSoloFibras(n);
                            }}
                          >
                            <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm" data-testid={`fiber-select-${i}`}>
                              <SelectValue placeholder="Selecciona fibra..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                              {fiberOptions.map(p => (
                                <SelectItem key={p.id} value={p.id} className="text-white text-sm">
                                  {p.nombre} — {fmtC(priceOf(p))}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {selectedFiber && (
                          <span className="text-slate-500 text-xs min-w-[65px] text-right">{fmtC(priceOf(selectedFiber))}</span>
                        )}
                        <button
                          onClick={() => {
                            const next = soloFibras.filter((_, j) => j !== i);
                            setSoloFibras(next.length ? next : [{ key: genLineId(), serviceId: "", ipAddOnId: "" }]);
                          }}
                          className="text-red-400 hover:text-red-300 p-1"
                          data-testid={`fiber-remove-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* IP adicional — only shown when catalog has IP_ADICIONAL services */}
                      {line.serviceId && ipOptions.length > 0 && (
                        <div className="flex items-center gap-2 pl-6">
                          <span className="text-slate-500 text-xs min-w-[75px]">+ IP adicional</span>
                          <div className="flex-1">
                            <Select
                              value={line.ipAddOnId || "__none__"}
                              onValueChange={v => {
                                const n = [...soloFibras];
                                n[i] = { ...n[i], ipAddOnId: v === "__none__" ? "" : v };
                                setSoloFibras(n);
                              }}
                            >
                              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-8 text-xs" data-testid={`fiber-ip-select-${i}`}>
                                <SelectValue placeholder="Sin IP adicional" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                                <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin IP adicional —</SelectItem>
                                {ipOptions.map(p => (
                                  <SelectItem key={p.id} value={p.id} className="text-white text-sm">
                                    {p.nombre} — {fmtC(priceOf(p))}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {selectedIp && (
                            <span className="text-slate-500 text-xs min-w-[55px] text-right">{fmtC(priceOf(selectedIp))}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSoloFibras([...soloFibras, { key: genLineId(), serviceId: "", ipAddOnId: "" }])}
                  className="text-[#03BEE4] text-xs h-7"
                  data-testid="add-fiber-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />+ Añadir fibra
                </Button>
              </div>

              {Object.keys(cats.saFibrasExtras).length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#1E1E5F]">
                  <Label className="text-slate-400 text-xs">Servicios adicionales</Label>
                  <div className="space-y-2 mt-2">
                    {Object.entries(cats.saFibrasExtras).map(([catName, services]) => {
                      const visible = services.filter(s => isSelectable(s));
                      if (visible.length === 0) return null;
                      return (
                      <div key={catName}>
                        <Label className="text-slate-500 text-xs mb-1">{catName}</Label>
                        <Select
                          value={soloFibrasExtras[catName] || "__none__"}
                          onValueChange={v => setSoloFibrasExtras(prev => ({ ...prev, [catName]: v === "__none__" ? "" : v }))}
                        >
                          <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm" data-testid={`block-c-extra-${catName}`}>
                            <SelectValue placeholder="Sin selección" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin selección —</SelectItem>
                            {visible.map(s => (
                              <SelectItem key={s.id} value={s.id} className="text-white text-sm">{s.nombre} — {fmtC(priceOf(s))}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Block D — Solo Móvil */}
        {isBlockVisible("D", modoBuilder) && (
        <BlockSoloMovil
          lines={soloMobiles}
          setLines={setSoloMobiles}
          products={cats.saMobiles}
          bonoProducts={cats.bonosIntl}
          bonoCheck={bonoIntlCheckD}
          existingLines={!blockBEnabled ? existingMobileLines : []}
          existingBonos={existingLineBonos}
          setExistingBonos={setExistingLineBonos}
          getProduct={getProduct}
          fmtC={fmtC}
          renderBlockHeader={renderBlockHeader}
          openBlocks={openBlocks}
          getPrice={priceOf}
          title={modoBuilder === "STANDALONE" ? "Solo Móvil" : "Móvil"}
        />
        )}

        {/* Block E — TV */}
        {isBlockVisible("E", modoBuilder) && (
        <div className="border border-[#1E1E5F] rounded-lg overflow-hidden" data-testid="block-E">
          {renderBlockHeader("E", <Tv className="h-4 w-4 text-[#03BEE4]" />, "TV", !tvCheck.permitido, tvCheck.mensaje)}
          {openBlocks.E && tvCheck.permitido && (
            <div className="p-4 space-y-3 bg-[#0A0A35]/30">
              {renderRows(tvSelections, setTvSelections, cats.tvProducts, undefined, "Selecciona TV...", undefined, "+ Añadir TV")}
              {cats.tvDiscounts.map(disc => {
                const elig = tvEligibilityFor(disc);
                const info = elig.eligibleCount > 0 ? `Aplicable a ${elig.eligibleCount} línea(s)` : null;
                return (
                  <React.Fragment key={disc.id}>
                    {renderDiscount(!!tvDiscountMap[disc.id], (checked) => setTvDiscountMap(prev => ({ ...prev, [disc.id]: checked })), disc, disc.nombre, info)}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Block F — Centralita */}
        {isBlockVisible("F", modoBuilder) && (
        <BlockCentralita
          centralitaId={centralitaId}
          setCentralitaId={setCentralitaId}
          centralitaKey={centralitaKey}
          extBonos={centralitaExtBonos}
          setExtBonos={setCentralitaExtBonos}
          terminalRows={terminalRows}
          setTerminalRows={setTerminalRows}
          ddiQuantity={ddiQuantity}
          setDdiQuantity={setDdiQuantity}
          eligibleCentDiscounts={eligibleCentDiscounts}
          centDiscountMap={centDiscountMap}
          setCentDiscountMap={setCentDiscountMap}
          cats={{
            centralitas: cats.centralitas.filter(isSelectable),
            terminals:   cats.terminals.filter(isSelectable),
            ddis:        cats.ddis.filter(isSelectable),
            bonosCent:   cats.bonosCent,
            descCent:    cats.descCent,
          }}
          bonoCentCheck={bonoCentCheck}
          checkDiscountAvailable={checkDiscountAvailable}
          currentServices={currentServices}
          existingCentralitas={existingCentralitas}
          existingCentralitaBonos={existingCentralitaBonos}
          setExistingCentralitaBonos={setExistingCentralitaBonos}
          getProduct={getProduct}
          fmtC={fmtC}
          renderBlockHeader={renderBlockHeader}
          renderDiscount={renderDiscount}
          openBlocks={openBlocks}
          getPrice={priceOf}
        />
        )}

        {/* Block G — WiFi Pyme */}
        {isBlockVisible("G", modoBuilder) && (
        <div className="border border-[#1E1E5F] rounded-lg overflow-hidden" data-testid="block-G">
          {renderBlockHeader("G", <Radio className="h-4 w-4 text-[#03BEE4]" />, "WiFi Pyme")}
          {openBlocks.G && (
            <div className="p-4 space-y-3 bg-[#0A0A35]/30">
              <div>
                <Label className="text-slate-300 text-sm">WiFi Pyme</Label>
                <Select value={wifiId || "__none__"} onValueChange={v => setWifiId(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm">
                    <SelectValue placeholder="Sin WiFi" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                    <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin selección —</SelectItem>
                    {cats.wifiProducts.filter(p => isSelectable(p)).map(p => <SelectItem key={p.id} value={p.id} className="text-white text-sm">{p.nombre} — {fmtC(priceOf(p))}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* TV-style multi-discount: same rule-driven pattern as Centralita. */}
              {eligibleWifiDiscounts.map(disc => (
                <React.Fragment key={disc.id}>
                  {renderDiscount(
                    !!wifiDiscountMap[disc.id],
                    (checked) => setWifiDiscountMap(prev => ({ ...prev, [disc.id]: checked })),
                    disc,
                    disc.nombre,
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Right column — Summary + rules-engine violations banner */}
      <div className="w-2/5 space-y-3 [&>div]:w-full">
        {!validation.ok && (
          <div
            className="rounded-lg border border-red-500/40 bg-red-500/10 p-3"
            data-testid="rules-validation-banner"
            role="alert"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-red-300 mb-1">
              Reglas de negocio no cumplidas
            </p>
            <ul className="space-y-1">
              {validation.violations.map(v => (
                <li key={v.ruleId} className="text-sm text-red-200 flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span data-testid={`rules-violation-${v.ruleId}`}>{v.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <PackageSummary computed={computed} fmtC={fmtC} manualDiscounts={manualDiscounts} />
      </div>
    </div>
  );
};
